<template>
    <div>
        <v-progress-circular indeterminate color="primary" class="mr-3" v-if="loading" />
        <v-window v-else v-model="currentStep">
            <v-window-item value="selectUsers">
                <v-row no-gutters style="flex-wrap: nowrap; gap: 8px" class="pb-2 justify-start">
                    <v-col cols="auto" class="d-flex my-2">
                        <v-btn size="small" @click="currentStep = 'createUser'">{{
                            $t("buttons.ajouter")
                        }}</v-btn>
                    </v-col>
                    <v-col cols="auto" class="d-flex my-2">
                        <v-btn size="small" @click="saveMailSubscription()">{{
                            $t("buttons.enregistrer")
                        }}</v-btn>
                    </v-col>
                    <v-col cols="auto" class="d-flex my-2">
                        <v-btn size="small" @click="subscribeEveryone()">{{
                            $t("buttons.selectionner-tout")
                        }}</v-btn>
                    </v-col>
                    <v-col cols="auto" class="d-flex my-2">
                        <v-btn size="small" @click="unSubscribeEveryone()">{{
                            $t("buttons.deselectionner-tout")
                        }}</v-btn>
                    </v-col>
                </v-row>

                <v-list subheader style="max-height: 65vh; overflow-y: auto" lines="one">
                    <template v-for="(user, i) in listUsers" :key="i">
                        <v-list-item class="rounded-xl border-thin mb-2">
                            <template v-slot:append>
                                <span>{{ getUserBigram(user) }}</span>
                            </template>
                            <template v-slot:default>
                                <v-row no-gutters>
                                    <v-col cols="6" class="d-flex align-center">
                                        {{ user.email }}
                                    </v-col>
                                    <v-col cols="6" class="d-flex align-center pl-4">
                                        {{ user.firstname }} {{ user.lastname }}
                                    </v-col>
                                </v-row>
                            </template>
                            <template v-slot:prepend>
                                <v-checkbox
                                    class="d-flex align-center"
                                    v-model="user.isSubscribe"
                                ></v-checkbox>
                            </template>
                        </v-list-item>
                    </template>
                </v-list>
            </v-window-item>
            <v-window-item value="createUser">
                <CreateFakeUser
                    :isBack="true"
                    :companyId="companyId"
                    :actions="actions"
                    @addUser="addNewUser"
                    @back="goBack()"
                ></CreateFakeUser>
            </v-window-item>
        </v-window>
    </div>
</template>

<script lang="ts">
import CreateFakeUser from "./CreateFakeUser.vue";

import GET_USERS from "@/graphql/user/SelectUserForUserSubscription.gql";
import UPSERT_USERS_SUBSCRIPTION from "@/graphql/user/UpsertUsersUserSubscription.gql";

import { getUserBigram } from "@/services/userServices";

import { Apollo } from "@/decorators";
import { Component, Vue, toNative, Prop, Watch, Emit } from "vue-facing-decorator";
import { DialogAction } from "@/services/dialogAction";

type User = {
    id: string;
    email: string;
    firstname: string;
    lastname: string;
    phoneNumber: string;
    isSubscribe: boolean;
    isSubscribeBase?: boolean;
};

@Component({
    components: {
        CreateFakeUser,
    },
})
export class SelectUserForUserSubscription extends Vue {
    @Prop({}) readonly mailToSubscribe: string;
    @Prop({}) readonly companyId: string;
    @Prop({ default: null }) actions: DialogAction;

    currentStep: string = "selectUsers";

    everyoneIsSubscribe: boolean = false;

    @Apollo({
        query: GET_USERS,
        variables() {
            return {
                companyId: this.companyId,
                mailToSubscribe: this.mailToSubscribe,
            };
        },
        skip() {
            return false;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.getUserForUserSubscription) {
                this.listUsers = data.getUserForUserSubscription.users;
                this.listUsers.forEach((user) => {
                    user.isSubscribeBase = user.isSubscribe;
                });
            }
        },
    })
    listUsers: User[] = [];

    get loading() {
        if (this.$apollo.queries.users) {
            return this.$apollo.queries.users.loading;
        }
        return false;
    }

    @Emit("previous")
    previous() {
        return;
    }

    @Watch("listUsers", { deep: true })
    checkIfEveryoneIsSubscribe() {
        let everyoneIsSubscribe = true;

        this.listUsers.forEach((user) => {
            if (!user.isSubscribe) {
                everyoneIsSubscribe = false;
            }
        });
        if (everyoneIsSubscribe) {
            this.everyoneIsSubscribe = true;
        } else {
            this.everyoneIsSubscribe = false;
        }
    }

    @Watch("actions", { immediate: true })
    initAction() {
        this.actions.previous = this.previous;
    }

    addNewUser(user) {
        this.listUsers.push({
            id: user.id,
            email: user.email,
            lastname: user.lastname,
            firstname: user.firstname,
            phoneNumber: user.phoneNumber,
            isSubscribe: true,
            isSubscribeBase: false,
        });
        this.currentStep = "selectUsers";
    }

    goBack() {
        this.currentStep = "selectUsers";
        if (this.actions) {
            this.actions.previous = () => {
                this.previous();
            };
        }
    }

    getUserBigram(user) {
        return getUserBigram(user);
    }

    subscribeEveryone() {
        this.listUsers.forEach((user) => {
            user.isSubscribe = true;
        });
        this.everyoneIsSubscribe = true;
    }

    unSubscribeEveryone() {
        this.listUsers.forEach((user) => {
            user.isSubscribe = false;
        });
        this.everyoneIsSubscribe = false;
    }

    saveMailSubscription() {
        const listUsersSubChange: User[] = [];

        this.listUsers.forEach((user) => {
            if (user.isSubscribe != user.isSubscribeBase) {
                listUsersSubChange.push({
                    id: user.id,
                    email: user.email,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    phoneNumber: user.phoneNumber,
                    isSubscribe: user.isSubscribe,
                });
            }
        });
        this.$apollo.mutate({
            mutation: UPSERT_USERS_SUBSCRIPTION,
            variables: {
                companyId: this.companyId,
                subscription: this.mailToSubscribe,
                users: listUsersSubChange,
            },
        });
    }
}

export default toNative(SelectUserForUserSubscription);
</script>
